/**
 * Appointment service functions
 * 
 * This module provides functions for finding and managing appointments
 */

import { findPracticesWithinDistance } from '../utils/locationUtils';
import { db } from '../firebase';
import { collection, addDoc, Timestamp, doc, getDoc, updateDoc, getDocs, query, where, orderBy } from 'firebase/firestore';

/**
 * Get all appointments for a specific date and practice
 * @param {string} practiceId - The ID of the practice
 * @param {string} date - The date in YYYY-MM-DD format
 * @returns {Promise<Array>} Array of appointment objects with their IDs
 */
export const getAppointmentsForDateAndPractice = async (practiceId, date) => {
  try {
    console.log(`Getting appointments for practice ${practiceId} on date ${date}`);
    
    const appointmentsRef = collection(db, 'appointments');
    const q = query(
      appointmentsRef,
      where('practiceId', '==', practiceId),
      where('date', '==', date)
    );
    
    const querySnapshot = await getDocs(q);
    
    const appointments = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    
    console.log(`Found ${appointments.length} appointments for practice ${practiceId} on date ${date}`);
    return appointments;
  } catch (error) {
    console.error('Error getting appointments for date and practice:', error);
    return [];
  }
};

/**
 * Check if a slot is available (not booked)
 * @param {string} availabilityId - The ID of the availability document
 * @param {number} slotIndex - The index of the slot in the slots array
 * @returns {Promise<boolean>} True if the slot is available, false if it's booked
 */
export const checkSlotAvailability = async (availabilityId, slotIndex) => {
  try {
    // Get the availability document
    const availabilityRef = doc(db, 'availability', availabilityId);
    const availabilityDoc = await getDoc(availabilityRef);
    
    if (!availabilityDoc.exists()) {
      console.error(`Availability document ${availabilityId} not found`);
      return false;
    }
    
    const availabilityData = availabilityDoc.data();
    
    // Make sure the slots array exists and the index is valid
    if (!availabilityData.slots || 
        !Array.isArray(availabilityData.slots) || 
        slotIndex >= availabilityData.slots.length) {
      console.error('Invalid slots array or slot index in availability document');
      return false;
    }
    
    // Check if the slot is already booked
    const slot = availabilityData.slots[slotIndex];
    
    // Log the slot status for debugging
    console.log(`Checking slot ${slotIndex} in availability ${availabilityId}:`, slot);
    console.log(`Slot isBooked:`, slot.isBooked);
    
    // Return true if the slot is not booked (isBooked is explicitly false)
    return slot.isBooked === false;
  } catch (error) {
    console.error('Error checking slot availability:', error);
    return false;
  }
};

/**
 * Get all appointments from Firestore
 * @returns {Promise<Array>} Array of appointment objects with their IDs
 */
export const getAllAppointments = async () => {
  try {
    const appointmentsRef = collection(db, 'appointments');
    const q = query(appointmentsRef, orderBy('createdAt', 'desc'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting appointments from Firestore:', error);
    throw error;
  }
};

/**
 * Update an appointment's status in Firestore
 * @param {string} appointmentId - The ID of the appointment to update
 * @param {string} status - The new status (e.g., 'pending', 'confirmed')
 * @returns {Promise<void>}
 */
export const updateAppointmentStatus = async (appointmentId, status) => {
  try {
    const appointmentRef = doc(db, 'appointments', appointmentId);
    
    // If status is 'confirmed', also update payment status
    // We keep the original payment status if it's 'authorized' to ensure the capturePayment function works correctly
    if (status === 'confirmed') {
      // First get the current appointment data to check the payment status
      const appointmentDoc = await getDoc(appointmentRef);
      const appointmentData = appointmentDoc.data();
      
      // Only change payment status to 'processing' if it's not already 'authorized'
      // This ensures the capturePayment function can still identify authorized payments
      const paymentStatus = appointmentData.paymentStatus === 'authorized' 
        ? 'authorized' 
        : 'processing';
      
      await updateDoc(appointmentRef, {
        status: status,
        paymentStatus: paymentStatus,
        updatedAt: Timestamp.now()
      });
    } else {
      await updateDoc(appointmentRef, {
        status: status,
        updatedAt: Timestamp.now()
      });
    }
  } catch (error) {
    console.error('Error updating appointment status in Firestore:', error);
    throw error;
  }
};

/**
 * Save an appointment to Firestore
 * @param {Object} appointmentData - The appointment data to save
 * @returns {Promise<string>} The ID of the created appointment
 */
export const saveAppointment = async (appointmentData) => {
  try {
    // Add to Firestore
    const appointmentsRef = collection(db, 'appointments');
    const docRef = await addDoc(appointmentsRef, {
      ...appointmentData,
      createdAt: appointmentData.createdAt || Timestamp.now()
    });
    
    // Mark the slot as booked in the availability collection
    if (appointmentData.slot && 
        appointmentData.slot.availabilityId && 
        appointmentData.slot.slotIndex !== undefined) {
      
      try {
        // Get the availability document
        const availabilityRef = doc(db, 'availability', appointmentData.slot.availabilityId);
        const availabilityDoc = await getDoc(availabilityRef);
        
        if (availabilityDoc.exists()) {
          const availabilityData = availabilityDoc.data();
          
          // Make sure the slots array exists and the index is valid
          if (availabilityData.slots && 
              Array.isArray(availabilityData.slots) && 
              appointmentData.slot.slotIndex < availabilityData.slots.length) {
            
            // Create a new slots array with the updated slot
            const updatedSlots = [...availabilityData.slots];
            updatedSlots[appointmentData.slot.slotIndex] = {
              ...updatedSlots[appointmentData.slot.slotIndex],
              isBooked: true,
              appointmentId: docRef.id
            };
            
            // Update the availability document
            await updateDoc(availabilityRef, {
              slots: updatedSlots,
              updatedAt: Timestamp.now()
            });
            
            console.log(`Marked slot ${appointmentData.slot.slotIndex} as booked in availability ${appointmentData.slot.availabilityId}`);
          } else {
            console.error('Invalid slots array or slot index in availability document');
          }
        } else {
          console.error(`Availability document ${appointmentData.slot.availabilityId} not found`);
        }
      } catch (err) {
        console.error('Error updating availability slot:', err);
        // Don't throw the error here, as we still want to return the appointment ID
        // even if marking the slot as booked fails
      }
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving appointment to Firestore:', error);
    throw error;
  }
};

/**
 * Find the next available appointment within a specified distance
 * @param {string} postcode - User's postcode
 * @param {Array} practices - Array of practice objects
 * @param {number} maxDistance - Maximum distance in kilometers
 * @param {Function} availabilityService - Service to check availability for a practice
 * @returns {Promise<Object>} Object containing the result of the search
 */
export const findNextAvailableAppointment = async (postcode, practices, maxDistance, availabilityService) => {
  if (!postcode || !practices || practices.length === 0 || !availabilityService) {
    return { 
      success: false, 
      error: 'Invalid parameters',
      nearestPracticeWithAvailability: null,
      nearestPracticeSlots: [],
      availabilityMap: {},
      sortedPractices: []
    };
  }
  
  try {
    console.log(`Finding next available appointment within ${maxDistance}km of ${postcode}`);
    
    // Find practices within the specified distance
    const practicesWithinDistance = await findPracticesWithinDistance(postcode, practices, maxDistance);
    
    console.log(`Found ${practicesWithinDistance.length} practices within ${maxDistance}km`);
    
    if (practicesWithinDistance.length === 0) {
      return { 
        success: false, 
        error: `No practices found within ${maxDistance}km of your location.`,
        nearestPracticeWithAvailability: null,
        nearestPracticeSlots: [],
        availabilityMap: {},
        sortedPractices: practicesWithinDistance
      };
    }
    
    // Check next 14 days for each practice
    const availabilityMap = {};
    let foundAvailability = false;
    let nearestPracticeWithAvailability = null;
    let nearestPracticeSlots = [];
    
    // Check today specifically for each practice first
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth() + 1; // JavaScript months are 0-indexed
    const todayDay = today.getDate();
    
    const todayString = `${todayYear}-${todayMonth.toString().padStart(2, '0')}-${todayDay.toString().padStart(2, '0')}`;
    
    console.log(`Checking today (${todayString}) for all practices first`);
    
    // First, check today for all practices
    for (const practice of practicesWithinDistance) {
      console.log(`Checking practice ${practice.name} (${practice.id}) for today`);
      
      try {
        const slots = await availabilityService.getPracticeAvailabilityForDate(practice.id, todayString);
        
        if (slots && slots.length > 0) {
          console.log(`Found ${slots.length} slots for practice ${practice.name} today`);
          
          // Store the first available date for this practice
          availabilityMap[practice.id] = {
            date: todayString,
            slots
          };
          
          // If this is the first practice with availability or it's closer than the current nearest
          if (!nearestPracticeWithAvailability || practice.distance < nearestPracticeWithAvailability.distance) {
            nearestPracticeWithAvailability = practice;
            nearestPracticeSlots = slots;
          }
          
          foundAvailability = true;
        } else {
          console.log(`No slots found for practice ${practice.name} today`);
        }
      } catch (err) {
        console.error(`Error checking availability for practice ${practice.id} on date ${todayString}:`, err);
      }
    }
    
    // If no availability found today, check the next 13 days
    if (!foundAvailability) {
      console.log(`No availability found today, checking next 13 days`);
      
      // Check each practice for the next available appointment
      for (const practice of practicesWithinDistance) {
        console.log(`Checking practice ${practice.name} (${practice.id}) for next 13 days`);
        
        // Check next 13 days (skipping today as we already checked it)
        for (let i = 1; i < 14; i++) {
          const checkDate = new Date();
          checkDate.setDate(checkDate.getDate() + i);
          
          const year = checkDate.getFullYear();
          const month = checkDate.getMonth() + 1; // JavaScript months are 0-indexed
          const day = checkDate.getDate();
          
          const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
          
          console.log(`Checking date ${dateString} for practice ${practice.name}`);
          
          try {
            const slots = await availabilityService.getPracticeAvailabilityForDate(practice.id, dateString);
            
            if (slots && slots.length > 0) {
              console.log(`Found ${slots.length} slots for practice ${practice.name} on ${dateString}`);
              
              // Store the first available date for this practice
              if (!availabilityMap[practice.id]) {
                availabilityMap[practice.id] = {
                  date: dateString,
                  slots
                };
                
                // If this is the first practice with availability or it's closer than the current nearest
                if (!nearestPracticeWithAvailability || practice.distance < nearestPracticeWithAvailability.distance) {
                  nearestPracticeWithAvailability = practice;
                  nearestPracticeSlots = slots;
                }
                
                foundAvailability = true;
              }
              
              // No need to check more dates for this practice
              break;
            } else {
              console.log(`No slots found for practice ${practice.name} on ${dateString}`);
            }
          } catch (err) {
            console.error(`Error checking availability for practice ${practice.id} on date ${dateString}:`, err);
          }
        }
      }
    }
    
    if (foundAvailability && nearestPracticeWithAvailability) {
      return {
        success: true,
        nearestPracticeWithAvailability,
        nearestPracticeSlots,
        availabilityMap,
        sortedPractices: practicesWithinDistance
      };
    } else {
      return {
        success: false,
        error: `No available appointments found within ${maxDistance}km. Try increasing the search distance.`,
        nearestPracticeWithAvailability: null,
        nearestPracticeSlots: [],
        availabilityMap,
        sortedPractices: practicesWithinDistance
      };
    }
  } catch (error) {
    console.error('Error finding next available appointment:', error);
    return {
      success: false,
      error: 'Failed to find available appointments. Please try again.',
      nearestPracticeWithAvailability: null,
      nearestPracticeSlots: [],
      availabilityMap: {},
      sortedPractices: []
    };
  }
};
